import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, User, ArrowRight, ArrowLeft, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { BLOG_POSTS } from '../data/blogData';

const BlogPage = () => {
    const [posts, setPosts] = useState(BLOG_POSTS);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    const loadMorePosts = useCallback(() => {
        if (loading || !hasMore) return;

        setLoading(true);

        // Simulate API call to fetch more posts
        setTimeout(() => {
            const nextId = posts.length + 1;
            const newPosts = [
                {
                    id: nextId,
                    title: `Advancing Business with AI - Part ${Math.floor(nextId / 3)}`,
                    excerpt: 'Practical applications of artificial intelligence in modern business workflows and decision making.',
                    date: 'Jan 28, 2026',
                    author: 'Tech Lab',
                    image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&q=80&w=800',
                    category: 'AI',
                },
                {
                    id: nextId + 1,
                    title: 'Cybersecurity Best Practices for 2026',
                    excerpt: 'Protect your digital assets with the latest security protocols and proactive monitoring strategies.',
                    date: 'Jan 22, 2026',
                    author: 'SecTeam',
                    image: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&q=80&w=800',
                    category: 'Security',
                },
                {
                    id: nextId + 2,
                    title: 'The Rise of Green Technology',
                    excerpt: 'Sustainable computing and how eco-friendly hardware is becoming a standard in the industry.',
                    date: 'Jan 15, 2026',
                    author: 'EcoTech',
                    image: 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?auto=format&fit=crop&q=80&w=800',
                    category: 'Environment',
                },
            ];

            setPosts(prev => [...prev, ...newPosts]);
            setLoading(false);

            // Stop after some posts for this demo
            if (posts.length >= 15) {
                setHasMore(false);
            }
        }, 1500);
    }, [loading, hasMore, posts.length]);

    useEffect(() => {
        const handleScroll = () => {
            if (
                window.innerHeight + document.documentElement.scrollTop + 50 >=
                document.documentElement.offsetHeight
            ) {
                loadMorePosts();
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [loadMorePosts]);

    return (
        <div className="pt-24 pb-20 bg-light min-h-screen">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-12 gap-6">
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                    >
                        <Link
                            to="/"
                            className="inline-flex items-center text-primary font-bold hover:gap-2 transition-all mb-4 group"
                        >
                            <ArrowLeft size={18} className="mr-2 transition-transform group-hover:-translate-x-1" /> Back to Home
                        </Link>
                        <h1 className="text-4xl lg:text-5xl font-bold text-dark">
                            Latest <span className="text-primary">Insights</span>
                        </h1>
                    </motion.div>

                    <motion.p
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="text-gray-600 max-w-md md:text-right"
                    >
                        A deep dive into the world of technology, design, and business innovation.
                    </motion.p>
                </div>

                {/* Blog Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    <AnimatePresence>
                        {posts.map((post, index) => (
                            <motion.div
                                key={post.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: (index % 3) * 0.1 }}
                                whileHover={{ y: -10 }}
                                className="bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-soft-lg transition-all duration-300 group"
                            >
                                <div className="relative h-48 overflow-hidden">
                                    <img
                                        src={post.image}
                                        alt={post.title}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                    />
                                    <div className="absolute top-4 left-4 bg-primary text-white text-xs font-bold px-3 py-1 rounded-full">
                                        {post.category}
                                    </div>
                                </div>

                                <div className="p-6">
                                    <div className="flex items-center text-sm text-gray-500 mb-4 gap-4">
                                        <span className="flex items-center gap-1">
                                            <Calendar size={14} /> {post.date}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <User size={14} /> {post.author}
                                        </span>
                                    </div>

                                    <h3 className="text-xl font-bold text-dark mb-3 group-hover:text-primary transition-colors">
                                        {post.title}
                                    </h3>
                                    <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-2">
                                        {post.excerpt}
                                    </p>

                                    <Link
                                        to={`/blog/${post.id}`}
                                        className="inline-flex items-center gap-2 text-primary font-bold hover:gap-3 transition-all"
                                    >
                                        Read More <ArrowRight size={16} />
                                    </Link>
                                </div>
                            </motion.div>
                        ))}
                    </AnimatePresence>
                </div>

                {/* Loading Indicator */}
                {hasMore && (
                    <div className="mt-12 flex justify-center">
                        {loading ? (
                            <div className="flex flex-col items-center gap-4">
                                <Loader2 className="animate-spin text-primary" size={32} />
                                <p className="text-gray-500 font-medium">Loading more stories...</p>
                            </div>
                        ) : (
                            <p className="text-gray-400 italic font-medium">Scroll down for more</p>
                        )}
                    </div>
                )}

                {!hasMore && (
                    <div className="mt-12 text-center text-gray-400 border-t border-gray-200 pt-8">
                        <p className="font-medium">You've reached the end of our current insights. Stay tuned for more!</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BlogPage;
